<script setup lang="ts">
import { type CSSProperties, computed } from "vue";

import { isset } from "@/helpers/object";

import { BadgeColor, type BadgeProps, BadgeSize } from "@/dsComponents/Badge/types";

// CSS properties applied to root component element (button)
// biome-ignore lint/correctness/noUnusedVariables: Init Biome
interface BadgeStyle extends CSSProperties {
    backgroundColor: string | undefined;
    borderColor: string | undefined;
    color: string;
}

const props = withDefaults(defineProps<BadgeProps>(), {
    size: BadgeSize.md,
    color: BadgeColor.white,
    iconColor: BadgeColor.neutral,
});

const emit = defineEmits(["click"]);

const handleClick = () => emit("click");

const style = computed(() => {
    const composeStyle = {} as CSSProperties;
    if (props.backgroundColor) composeStyle.backgroundColor = props.backgroundColor;
    if (props.fontColor && !props.iconColorOverride) composeStyle.color = props.fontColor;
    if (props.borderColor) composeStyle.borderColor = props.borderColor;
    if (props.contentPadding !== undefined) composeStyle.padding = props.contentPadding;
    if (props.contentGap !== undefined) composeStyle.gap = props.contentGap;
    if (props.height !== undefined) composeStyle.height = props.height;
    if (props.minHeight !== undefined) composeStyle.minHeight = props.minHeight;
    return composeStyle;
});

const labelStyle = computed((): CSSProperties => {
    const composeStyle = {} as CSSProperties;
    if (props.fontColor && props.iconColorOverride) {
        composeStyle.color = props.fontColor;
    }
    if (props.labelPadding !== undefined) {
        composeStyle.padding = props.labelPadding;
    }
    return composeStyle;
});

const iconClassList = computed(() => {
    const classes = ["ti", `ti-${props.icon}`];
    if (props.fontColor && !props.iconColorOverride) {
        return classes;
    }
    classes.push(props.iconColor);
    return classes;
});

const iconStyle = computed(() => {
    const composeStyle = {} as CSSProperties;
    if (props.iconColorOverride) {
        composeStyle.color = props.iconColorOverride;
    } else if (props.fontColor) {
        composeStyle.color = props.fontColor;
    }
    return composeStyle;
});
</script>
<template>
    <span
        v-if="label || icon"
        :class="[
            'ds-badge-component',
            size,
            color,
            { stroke, iconOnly: !isset(label), clickable },
        ]"
        :style="style"
        @click="handleClick"
    >
        <i v-if="icon" :class="iconClassList" :style="iconStyle"></i>
        <span class="badge-text" v-if="isset(label)" :style="labelStyle">{{ String(label) }}</span>
    </span>
</template>
<style scoped lang="scss">
@mixin get-bg-stroke($bg, $stroke) {
    background-color: $bg;

    &.stroke {
        border-color: $stroke;
    }
}

@mixin get-size($size) {
    height: $size;

    &.iconOnly {
        width: $size;
        border-radius: 50px;
        padding: 0;

        .ti {
            margin: auto;
        }
    }
}

.ds-badge-component {
    width: fit-content;
    display: flex;
    gap: 6px;
    color: $text-main-900;
    white-space: nowrap;
    align-items: center;
    justify-content: center;

    .badge-text {
        padding: 0 2px;
    }

    .ti {
        &.white {
            color: $white;
        }

        &.neutral {
            color: $text-main-900;
        }

        &.primary {
            color: $primary-base;
        }

        &.purple {
            color: $purple-base;
        }

        &.red {
            color: $red-base;
        }

        &.orange {
            color: $orange-base;
        }

        &.green {
            color: $green-base;
        }

        &.yellow {
            color: $yellow-base;
        }

        &.salesforce {
            color: $salesforce;
        }
    }

    &.xl {
        border-radius: 12px;
        padding: 0 8px;
        @include get-size(28px);
        @include font-size-16;

        .ti {
            font-size: 18px;
        }
    }

    &.lg {
        border-radius: 8px;
        padding: 0 6px;
        @include get-size(24px);
        @include font-size-14;

        .ti {
            font-size: 16px;
        }
    }

    &.md {
        border-radius: 8px;
        padding: 0 6px;
        gap: 4px;
        @include get-size(22px);
        @include font-size-12;

        .ti {
            font-size: 14px;
        }
    }

    &.sm {
        border-radius: 8px;
        padding: 0 4px;
        @include get-size(20px);
        @include font-size-12;

        .ti {
            font-size: 14px;
        }
    }

    &.stroke {
        border: 1px solid;
    }

    &.clickable {
        cursor: pointer;
    }

    &.white {
        @include get-bg-stroke($bg-white-0, $stroke-soft-200);
    }

    &.neutral {
        @include get-bg-stroke($bg-weak-100, $stroke-soft-200);
    }

    &.primary {
        @include get-bg-stroke($primary-lighter, $primary-light);
    }

    &.purple {
        @include get-bg-stroke($purple-lighter, $purple-light);
    }

    &.red {
        @include get-bg-stroke($red-lighter, $red-light);
    }

    &.orange {
        @include get-bg-stroke($orange-lighter, $orange-light);
    }

    &.green {
        @include get-bg-stroke($green-lighter, $green-light);
    }

    &.yellow {
        @include get-bg-stroke($yellow-lighter, $yellow-light);
    }

    &.transparent {
        @include get-bg-stroke(transparent, transparent);
    }
}
</style>
